/**
 * @fileoverview Tabela Editável de DRE
 * @module components/dre/DREEditableTable
 * 
 * Componente principal para visualização e edição da DRE
 * Usa MUI Table com células editáveis inline
 */

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';

import { useDREStore } from '../../stores/dreStore';
import { useExportDRE } from '../../hooks/useExportDRE';
import { useUndoRedoShortcuts } from '../../hooks/useUndoRedoShortcuts';
import { useVersionStore } from '../../stores/versionStore';
import { DRE_LINES } from '../../types/dre';
import { DRECell } from './DRECell';
import { AddYearDialog } from './AddYearDialog';
import { RemoveYearButton } from './RemoveYearButton';
import { RestoreConfirmationModal } from '../modals/RestoreConfirmationModal';

interface DREEditableTableProps {
  company: 'setima' | '555';
}

export function DREEditableTable({ company }: DREEditableTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const {
    dreData,
    isEditing,
    isSaving,
    setEditing,
    saveDRE,
    restoreToOriginal,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useDREStore();

  const { exportDREToExcel } = useExportDRE();
  const { saveVersion } = useVersionStore();
  
  // Ativar keyboard shortcuts
  useUndoRedoShortcuts();

  const [addYearDialogOpen, setAddYearDialogOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);

  /**
   * Calcula edições entre dados atuais e dados originais
   * TODO: Implementar comparação real com dados master do banco
   */
  const calculateEdits = () => {
    // Por enquanto, retorna edições mock para demonstração
    // Em produção, isso deve comparar dreData atual com MASTER_DRE_DATA do banco
    return [
      {
        company: 'Sétima' as const,
        year: 2020,
        field: 'Receita Bruta',
        oldValue: 6.56,
        newValue: 4.175,
        percentChange: -36.4,
      },
      {
        company: 'Sétima' as const,
        year: 2020,
        field: 'Margem Bruta',
        oldValue: 65.0,
        newValue: 52.1,
        percentChange: -19.8,
      },
    ];
  };

  /**
   * Abre modal de confirmação em vez de restaurar diretamente
   */
  const handleRestoreClick = () => {
    const edits = calculateEdits();
    
    if (edits.length === 0) {
      toast('Nenhuma edição para restaurar. Os dados já estão originais.', {
        icon: 'ℹ️',
      });
      return;
    }

    setRestoreModalOpen(true);
  };

  /**
   * Confirma restauração: cria checkpoint e executa
   */
  const handleConfirmRestore = async () => {
    const edits = calculateEdits();
    
    try {
      // 1. Criar checkpoint automático
      const checkpointName = `Checkpoint antes de restaurar dados originais`;
      const checkpointDescription = `Snapshot automático criado antes de restaurar. ${edits.length} edição(ões) salvas neste checkpoint.`;
      
      await saveVersion(checkpointName, checkpointDescription, true); // true = is_auto_checkpoint
      
      // 2. Executar restauração
      restoreToOriginal();
      
      // 3. Fechar modal
      setRestoreModalOpen(false);
      
      toast.success('Checkpoint salvo no histórico', {
        duration: 4000,
      });
    } catch (error) {
      console.error('[DREEditableTable] Erro ao criar checkpoint:', error);
      toast.error('Erro ao criar checkpoint. Restauração cancelada.');
    }
  };

  const companyData = company === 'setima' ? dreData?.setima : dreData?.studio555;

  if (!companyData) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">Carregando dados...</Typography>
      </Paper>
    );
  }

  const handleSave = async () => {
    await saveDRE();
    setEditing(false);
  };

  const handleCancel = () => {
    // Recarregar dados originais
    useDREStore.getState().loadDRE();
    setEditing(false);
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* Header com controles */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          DRE {companyData.companyName}
        </Typography>

        <Stack direction="row" spacing={1}>
          {!isEditing ? (
            <>
              <Button
                startIcon={<RestartAltIcon />}
                variant="outlined"
                color="error"
                onClick={handleRestoreClick}
              >
                Restaurar Dados Originais
              </Button>
              <Button
                startIcon={<FileDownloadIcon />}
                variant="outlined"
                onClick={exportDREToExcel}
              >
                Exportar Excel
              </Button>
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setEditing(true)}
              >
                Editar DRE
              </Button>
            </>
          ) : (
            <>
              <Tooltip title="Desfazer (Ctrl+Z)">
                <span>
                  <IconButton
                    size="small"
                    onClick={undo}
                    disabled={!canUndo()}
                  >
                    <UndoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refazer (Ctrl+Shift+Z)">
                <span>
                  <IconButton
                    size="small"
                    onClick={redo}
                    disabled={!canRedo()}
                  >
                    <RedoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                startIcon={<CloseIcon />}
                variant="outlined"
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* DIA 10: Dica de scroll em mobile */}
      {isMobile && companyData.years.length > 3 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          💡 <strong>Dica:</strong> Arraste a tabela horizontalmente para ver todos os anos
        </Alert>
      )}

      {/* Tabela com scroll horizontal para mobile */}
      <TableContainer 
        sx={{ 
          maxHeight: 600, 
          overflowX: 'auto',
          // DIA 10: Scroll suave em mobile/touch devices
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.2)',
            borderRadius: 4,
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 250, fontWeight: 'bold', bgcolor: 'background.paper' }}>
                Linha DRE
              </TableCell>
              {companyData.years.map((year) => (
                <TableCell
                  key={year.year}
                  align="right"
                  sx={{ minWidth: 150, fontWeight: 'bold', bgcolor: 'background.paper' }}
                >
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                    <span>{year.year}</span>
                    {isEditing && (
                      <RemoveYearButton company={company} year={year.year} />
                    )}
                  </Stack>
                </TableCell>
              ))}
              {isEditing && (
                <TableCell align="center" sx={{ minWidth: 100, bgcolor: 'background.paper' }}>
                  <Tooltip title="Adicionar ano">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setAddYearDialogOpen(true)}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {DRE_LINES.map((lineConfig) => (
              <TableRow
                key={lineConfig.field}
                sx={{
                  bgcolor: lineConfig.highlight 
                    ? (theme.palette.mode === 'dark' 
                        ? 'rgba(129, 213, 204, 0.15)'  // Verde teal translúcido para dark
                        : 'rgba(129, 213, 204, 0.1)')  // Verde teal mais suave para light
                    : 'transparent',
                  '&:hover': { 
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.05)' 
                      : 'rgba(0, 0, 0, 0.04)' 
                  },
                }}
              >
                <TableCell
                  sx={{
                    pl: lineConfig.indentLevel ? `${lineConfig.indentLevel * 2}rem` : '1rem',
                    fontWeight: lineConfig.highlight ? 'bold' : 'normal',
                    fontStyle: lineConfig.format === 'percent' ? 'italic' : 'normal',
                  }}
                >
                  {lineConfig.label}
                </TableCell>
                
                {companyData.years.map((year) => (
                  <DRECell
                    key={`${year.year}-${lineConfig.field}`}
                    company={company}
                    year={year.year}
                    field={lineConfig.field}
                    value={year[lineConfig.field]}
                    editable={isEditing && lineConfig.editable}
                    format={lineConfig.format}
                    negative={lineConfig.negative}
                    calculated={!lineConfig.editable}
                    highlight={lineConfig.highlight}
                  />
                ))}
                
                {isEditing && <TableCell />}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para adicionar ano */}
      <AddYearDialog
        open={addYearDialogOpen}
        company={company}
        existingYears={companyData.years.map(y => y.year)}
        onClose={() => setAddYearDialogOpen(false)}
      />

      {/* Modal de confirmação para restaurar dados originais */}
      <RestoreConfirmationModal
        open={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        onConfirm={handleConfirmRestore}
        edits={calculateEdits()}
      />
    </Paper>
  );
}
